import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FORUM_CATEGORIES } from "../data";
import { Icon } from "../Icon";
import { PageHead, useSeo } from "../ui";

/* 서버 시각(UTC, 시간대 표기 없음) → 2026.10.12 */
export function fmtDate(iso, withTime = false) {
  if (!iso) return "";
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  const day = `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())}`;
  return withTime ? `${day} ${p(d.getHours())}:${p(d.getMinutes())}` : day;
}

function PostRow({ post }) {
  return (
    <li>
      <Link to={`/forum/${post.id}`} className={`forum-item ${post.cover ? "" : "forum-item--text"}`}>
        <div className="forum-item__main">
          <div className="forum-item__top">
            {post.pinned && <span className="forum-tag forum-tag--pin">공지</span>}
            <span className="forum-tag">{post.category_label}</span>
          </div>
          <h2 className="forum-item__title">{post.title}</h2>
          {post.summary && <p className="forum-item__summary">{post.summary}</p>}
          <div className="forum-item__meta">
            <span>{fmtDate(post.created_at)}</span>
            <span>조회 {post.views.toLocaleString("ko-KR")}</span>
            <span>댓글 {post.comments}</span>
            {post.attachments > 0 && (
              <span className="forum-item__file">
                <Icon.Download width="14" height="14" aria-hidden="true" focusable="false" />
                첨부 {post.attachments}
              </span>
            )}
          </div>
        </div>
        {post.cover && (
          <div className="forum-item__thumb">
            <img src={post.cover} alt="" loading="lazy" />
          </div>
        )}
      </Link>
    </li>
  );
}

/* 포럼 오른쪽 칸 — 전체 글 또는 고른 분류의 글 목록. 분류 고르기는 왼쪽 메뉴(ForumLayout)가 맡는다. */
export default function Forum() {
  const [params, setParams] = useSearchParams();
  const cat = FORUM_CATEGORIES.some((c) => c.id === params.get("cat")) ? params.get("cat") : "";
  const catLabel = FORUM_CATEGORIES.find((c) => c.id === cat)?.label || "";

  useSeo({
    title: catLabel
      ? `${catLabel} — 포럼 | I.META`
      : "포럼 — STM32·ESP32·라즈베리파이·소프트웨어·3D프린팅 개발 노트 | I.META",
    description:
      "I.META가 직접 개발하며 정리한 STM32 · ESP32 · 라즈베리파이 펌웨어, 소프트웨어, 3D 프린팅 자료와 설명을 공개합니다. 궁금한 점은 글 아래 댓글로 문의하실 수 있습니다.",
    path: cat ? `/forum?cat=${cat}` : "/forum",
  });
  const q = params.get("q") || "";
  const page = Math.max(1, Number(params.get("page")) || 1);

  const [draft, setDraft] = useState(q);
  const [state, setState] = useState({ status: "loading", data: null });

  useEffect(() => { setDraft(q); }, [q]);

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, status: "loading" }));
    const qs = new URLSearchParams();
    if (cat) qs.set("category", cat);
    if (q) qs.set("q", q);
    if (page > 1) qs.set("page", String(page));
    (async () => {
      try {
        const res = await fetch(`/api/forum?${qs}`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (alive) setState({ status: "ready", data });
      } catch {
        if (alive) setState({ status: "error", data: null });
      }
    })();
    return () => { alive = false; };
  }, [cat, q, page]);

  /* 조건을 바꾸면 1쪽부터 — 비어 있는 값은 주소에서 뺀다 */
  function go(next) {
    const merged = { cat, q, page: 1, ...next };
    const out = {};
    if (merged.cat) out.cat = merged.cat;
    if (merged.q) out.q = merged.q;
    if (merged.page > 1) out.page = String(merged.page);
    setParams(out);
  }

  function search(e) {
    e.preventDefault();
    go({ q: draft.trim() });
  }

  const data = state.data;

  return (
    <section className="forum">
      <PageHead
        kicker={`FORUM${catLabel ? ` / ${catLabel}` : ""}`}
        title={catLabel || "포럼"}
        actions={
          <form className="forum__search" onSubmit={search} role="search">
            <input
              type="search"
              className="form__input"
              placeholder={catLabel ? `${catLabel}에서 검색` : "제목 · 내용 검색"}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              aria-label="포럼 검색"
            />
            <button type="submit" className="forum__search-btn" aria-label="검색">
              <Icon.Search width="18" height="18" aria-hidden="true" focusable="false" />
            </button>
          </form>
        }
      />

      {q && (
        <p className="forum__result">
          ‘{q}’ 검색 결과 {data ? data.total : "…"}건{" "}
          <button type="button" className="link-btn" onClick={() => go({ q: "" })}>검색 지우기</button>
        </p>
      )}

      {state.status === "error" && (
        <p className="res-empty">목록을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.</p>
      )}

      {state.status === "loading" && !data && <p className="res-empty">글 목록을 불러오는 중입니다…</p>}

      {data && data.items.length === 0 && state.status !== "loading" && (
        <div className="res-empty res-empty--box">
          <h2 className="res-empty__title">{q ? "검색 결과가 없습니다" : "아직 글이 없습니다"}</h2>
          <p>
            {q ? "다른 낱말로 찾아보세요. " : "곧 새 글로 찾아뵙겠습니다. "}
            필요하신 자료가 있으시면 <Link to="/contact">문의</Link>로 알려주세요.
          </p>
        </div>
      )}

      {data && data.items.length > 0 && (
        <ul className={`forum-list ${state.status === "loading" ? "is-loading" : ""}`}>
          {data.items.map((p) => <PostRow key={p.id} post={p} />)}
        </ul>
      )}

      {data && data.pages > 1 && (
        <nav className="forum-pager" aria-label="쪽 이동">
          <button type="button" disabled={page <= 1} onClick={() => go({ page: page - 1 })}>이전</button>
          {Array.from({ length: data.pages }, (_, i) => i + 1).map((n) => (
            <button
              key={n}
              type="button"
              className={n === page ? "is-active" : ""}
              aria-current={n === page ? "page" : undefined}
              onClick={() => go({ page: n })}
            >
              {n}
            </button>
          ))}
          <button type="button" disabled={page >= data.pages} onClick={() => go({ page: page + 1 })}>다음</button>
        </nav>
      )}
    </section>
  );
}
